"use client"

import { LucideIcon, Waves, Trees, Utensils, Dumbbell, ShoppingBag, Building2, School, Ship } from "lucide-react";
import { motion } from "motion/react";

interface CommunityAmenitiesProps {
    amenities: string[];
}

// ... (ICON_MAP remains the same)
const ICON_MAP: Record<string, LucideIcon> = {
    "Private Beach": Waves,
    "Fine Dining": Utensils,
    "Marina Access": Ship,
    "Shopping": ShoppingBag,
    "Nightlife": Utensils,
    "Opera District": Building2,
    "Golf Course": Trees, // Closest approx
    "Parks": Trees,
    "Schools": School,
    "Yacht Club": Ship,
    "Waterfront Dining": Utensils,
    "Beach Access": Waves,
    "Private Island": Waves,
    "Luxury Hotels": Building2,
    "Marina": Ship,
    "Gated Community": Building2,
    "Lakes": Waves,
    "Ain Dubai": Building2, // Generic
    "Retail": ShoppingBag,
    "Resort Living": Waves
};

export function CommunityAmenities({ amenities }: CommunityAmenitiesProps) {
    return (
        <section className="py-24 bg-secondary/5">
            <div className="container mx-auto px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center max-w-3xl mx-auto mb-16"
                >
                    <span className="text-xs font-bold tracking-[0.3em] text-secondary uppercase mb-4 block">
                        Facilities
                    </span>
                    <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl text-primary leading-[0.9] mb-6">
                        World-Class <span className="italic text-primary/50">Amenities</span>
                    </h2>
                    <p className="text-muted-foreground">
                        Immerse yourself in a world of convenience and luxury with amenities designed to cater to your every need.
                    </p>
                </motion.div>

                <motion.div
                    className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-16"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                    variants={{
                        visible: { transition: { staggerChildren: 0.1 } }
                    }}
                >
                    {amenities.map((amenity, index) => {
                        const Icon = ICON_MAP[amenity] || Building2; // Fallback icon
                        return (
                            <motion.div
                                key={amenity}
                                variants={{
                                    hidden: { opacity: 0, y: 30 },
                                    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
                                }}
                                className="flex flex-col items-center text-center group p-6 rounded-xl hover:bg-background hover:shadow-sm transition-all duration-300"
                            >
                                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full border border-primary/20 flex items-center justify-center mb-6 group-hover:bg-primary group-hover:border-primary transition-all duration-300 bg-white shadow-sm">
                                    <Icon className="w-6 h-6 md:w-8 md:h-8 text-primary group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                                </div>
                                <h3 className="font-serif text-lg md:text-xl text-primary">{amenity}</h3>
                            </motion.div>
                        )
                    })}
                    {/* Add some generic ones to fill grid if list is short */}
                    {amenities.length < 4 && (
                        <motion.div
                            variants={{
                                hidden: { opacity: 0, y: 30 },
                                visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
                            }}
                            className="flex flex-col items-center text-center group p-6 rounded-xl hover:bg-background hover:shadow-sm transition-all duration-300"
                        >
                            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full border border-primary/20 flex items-center justify-center mb-6 group-hover:bg-primary group-hover:border-primary transition-all duration-300 bg-white shadow-sm">
                                <Dumbbell className="w-6 h-6 md:w-8 md:h-8 text-primary group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                            </div>
                            <h3 className="font-serif text-lg md:text-xl text-primary">Wellness Centre</h3>
                        </motion.div>
                    )}
                </motion.div>
            </div>
        </section>
    );
}
